import React, { useState } from "react";
import { toast } from "react-toastify";
import { AddCircle, HelpOutline, Mic, Share as ShareIcon, Stop } from "@mui/icons-material";
import { IconButton as MuiIconButton, Tooltip } from "@mui/material";
import Image from "next/image";
import { ButtonType } from "components/atoms/Button/types";
import { ChevronDown } from "components/atoms/Icon";
import PopoverWithChildren from "components/atoms/Popover";
import { Label, Span } from "utils/styling/typography.styled";
import { AppBar, Button, CustomDialog, CustomDialogActions, CustomDialogContent, CustomDialogTitle, CustomIconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FirstRow, PopoverContentWrapper, PopoverTextBox, SecondRow, SessionButton, SessionColumn, SmallButton, StyledMenu, StyledMenuItem, Text, TextField, Toolbar } from "./SessionToolbar.styled";
interface Session {
  label: string;
  value: string;
}
interface SessionToolbarProps {
  isOwner: boolean;
  isRecording: boolean;
  stopRecording: () => void;
  startRecording: () => void;
  sessionId: string | null | undefined;
  sessions: Session[];
  handleSessionChange: (session: Session) => void;
  transcriptionName: string;
  totalCost: number;
  disableSessionSelection: boolean;
}
const SessionToolbar = ({
  isOwner,
  isRecording,
  stopRecording,
  startRecording,
  sessionId,
  sessions,
  handleSessionChange,
  transcriptionName,
  totalCost,
  disableSessionSelection
}: SessionToolbarProps) => {
  const [sessionMenuAnchor, setSessionMenuAnchor] = useState<null | HTMLElement>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newSessionName, setNewSessionName] = useState("");
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const copyToClipboard = () => {
    const sessionUrl = `https://char-gen.com/session-companion/${sessionId}`;
    navigator.clipboard.writeText(sessionUrl);
    toast.success("Link Copied to Clipboard!", {
      position: "bottom-center"
    });
  };
  const handleSessionMenuOpen = (event: React.MouseEvent<HTMLElement>) => setSessionMenuAnchor(event.currentTarget);
  const handleSessionMenuClose = () => setSessionMenuAnchor(null);
  const handleSessionSelect = (sessionValue: any) => {
    handleSessionChange(sessionValue);
    handleSessionMenuClose();
  };
  const handleCreateSession = () => {
    setIsModalOpen(true);
    handleSessionMenuClose();
  };
  const handleModalClose = () => {
    setIsModalOpen(false);
    setNewSessionName("");
  };
  const handleCreateSessionSubmit = () => {
    const newSession = {
      label: newSessionName,
      value: newSessionName
    };
    handleSessionChange(newSession);
    handleModalClose();
  };
  return <AppBar position="static" data-sentry-element="AppBar" data-sentry-component="SessionToolbar" data-sentry-source-file="index.tsx">
            <Toolbar variant="dense" data-sentry-element="Toolbar" data-sentry-source-file="index.tsx">
                {/* First Row: Session Choice + Record Button */}
                <FirstRow data-sentry-element="FirstRow" data-sentry-source-file="index.tsx">
                    <SessionColumn data-sentry-element="SessionColumn" data-sentry-source-file="index.tsx">
                        <Tooltip title="Select Session" data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                            {!disableSessionSelection ? <>
                                    <SessionButton aria-controls="session-menu" aria-haspopup="true" onClick={handleSessionMenuOpen}>
                                        {transcriptionName || "Select Session"}
                                        <Image height={24} width={24} src={ChevronDown} alt="Toggle Accordion" />
                                    </SessionButton>
                                    {/* Create New Button below the dropdown */}
                                    <SmallButton onClick={handleCreateSession}>
                                        <AddCircle style={{
                  marginRight: "5px"
                }} />
                                        New Session
                                    </SmallButton>
                                </> : <Span>{transcriptionName}</Span>}
                        </Tooltip>
                    </SessionColumn>

                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">Total Cost: {totalCost} Gold</Text>
                </FirstRow>

                {/* Second Row: Total Cost + Share Button */}
                <SecondRow data-sentry-element="SecondRow" data-sentry-source-file="index.tsx">
                    {isOwner && <Tooltip title={isRecording ? "Stop Recording" : "Start Recording"}>
                            <CustomIconButton onClick={isRecording ? stopRecording : startRecording} disabled={!sessionId} $isRecording={isRecording}>
                                {isRecording ? <Stop /> : <Mic />}
                            </CustomIconButton>
                        </Tooltip>}
                    {sessionId && <PopoverWithChildren buttonNode={<Tooltip title="Share Session">
                                    <MuiIconButton edge="end">
                                        <ShareIcon style={{
              color: "white"
            }} />
                                    </MuiIconButton>
                                </Tooltip>} position="bottom">
                            <PopoverContentWrapper>
                                <Label>Shareable URL:</Label>
                                <PopoverTextBox>
                                    https://char-gen.com/session-companion/
                                    {sessionId}
                                </PopoverTextBox>
                                <Button onClick={copyToClipboard}>
                                    Copy Link
                                </Button>
                            </PopoverContentWrapper>
                        </PopoverWithChildren>}
                    <Tooltip title={"Get help on how to use the Session-Companion"} data-sentry-element="Tooltip" data-sentry-source-file="index.tsx">
                        <MuiIconButton edge="end" onClick={() => setIsInfoOpen(true)} data-sentry-element="MuiIconButton" data-sentry-source-file="index.tsx">
                            <HelpOutline style={{
              color: "white"
            }} data-sentry-element="HelpOutline" data-sentry-source-file="index.tsx" />
                        </MuiIconButton>
                    </Tooltip>
                </SecondRow>
            </Toolbar>

            {/* Menu for Session Selection */}
            <StyledMenu id="session-menu" anchorEl={sessionMenuAnchor} keepMounted open={Boolean(sessionMenuAnchor)} onClose={handleSessionMenuClose} data-sentry-element="StyledMenu" data-sentry-source-file="index.tsx">
                {sessions.map(session => <StyledMenuItem key={session.value} onClick={() => handleSessionSelect(session)}>
                        {session.label}
                    </StyledMenuItem>)}
            </StyledMenu>

            <Dialog open={isModalOpen} onClose={handleModalClose} data-sentry-element="Dialog" data-sentry-source-file="index.tsx">
                <DialogTitle data-sentry-element="DialogTitle" data-sentry-source-file="index.tsx">Create New Session</DialogTitle>
                <DialogContent data-sentry-element="DialogContent" data-sentry-source-file="index.tsx">
                    <DialogContentText data-sentry-element="DialogContentText" data-sentry-source-file="index.tsx">
                        Please enter a name for your new session.
                    </DialogContentText>
                    <TextField autoFocus margin="dense" label="Session Name" type="text" fullWidth variant="outlined" // Use outlined variant for better control
        value={newSessionName} onChange={e => setNewSessionName(e.target.value)} data-sentry-element="TextField" data-sentry-source-file="index.tsx" />
                </DialogContent>
                <DialogActions data-sentry-element="DialogActions" data-sentry-source-file="index.tsx">
                    <Button variant={ButtonType.PRIMARY} onClick={handleModalClose} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Cancel
                    </Button>
                    <Button variant={ButtonType.SECONDARY} onClick={handleCreateSessionSubmit} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Create
                    </Button>
                </DialogActions>
            </Dialog>
            <CustomDialog open={isInfoOpen} onClose={() => setIsInfoOpen(false)} data-sentry-element="CustomDialog" data-sentry-source-file="index.tsx">
                <CustomDialogTitle data-sentry-element="CustomDialogTitle" data-sentry-source-file="index.tsx">
                    How to Use the Session Companion
                </CustomDialogTitle>
                <CustomDialogContent dividers data-sentry-element="CustomDialogContent" data-sentry-source-file="index.tsx">
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Welcome to the <strong>Session Companion Beta</strong>!
                        The Session Companion is designed to enhance your
                        role-playing sessions by recording audio and generating
                        images in real-time based on what it hears.
                    </Text>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        <strong>How It Works:</strong>
                    </Text>
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        The Session Companion listens to your audio, attempts to
                        understand key moments, and generates images based on
                        descriptions of characters, landscapes, or objects
                        during the session.
                    </Text>
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        You can also share the session with your campaign
                        members using a unique session link. They can follow
                        along and view images as they are generated in
                        real-time.
                    </Text>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        <strong>Cost System:</strong>
                    </Text>
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        Running costs for using the Session Companion are as
                        follows:
                    </Text>
                    <ul>
                        <li>0.5 gold per minute of audio recording.</li>
                        <li>1 gold per image generated.</li>
                    </ul>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        <strong>How to Use:</strong>
                    </Text>
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        - Start or stop recording by pressing the microphone
                        button. The system will process the audio in real-time
                        while you are speaking.
                    </Text>
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        - Share the session with others by clicking the share
                        button. This will provide a unique URL that others can
                        use to follow along.
                    </Text>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">
                        <strong>Beta Disclaimer:</strong> This tool is currently
                        in Beta, and we are actively improving it. You may
                        experience some inaccuracies in the images generated,
                        but we hope you enjoy the creative journey.
                    </Text>
                    <br />
                    <Text data-sentry-element="Text" data-sentry-source-file="index.tsx">Let the adventure begin!</Text>
                </CustomDialogContent>
                <CustomDialogActions data-sentry-element="CustomDialogActions" data-sentry-source-file="index.tsx">
                    <Button onClick={() => setIsInfoOpen(false)} variant={ButtonType.PRIMARY} data-sentry-element="Button" data-sentry-source-file="index.tsx">
                        Got it!
                    </Button>
                </CustomDialogActions>
            </CustomDialog>
        </AppBar>;
};
export default SessionToolbar;